import { useEffect, useState } from 'react';
import { CATEGORIES, CONSENT_VERSION } from '@/config/consent';
import { getConsent, setConsent, initConsentDefaults } from '@/lib/consent';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';

export default function ConsentManager() {
  const [bannerOpen, setBannerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [choices, setChoices] = useState({ 
    analytics: false, 
    marketing: false, 
    preferences: false 
  });

  useEffect(() => {
    initConsentDefaults();
    const saved = getConsent();
    if (!saved) {
      setBannerOpen(true);
    } else {
      setChoices({
        analytics: saved.analytics,
        marketing: saved.marketing,
        preferences: saved.preferences
      });
    }
  }, []);

  const acceptAll = () => {
    setConsent({ analytics: true, marketing: true, preferences: true, necessary: true });
    setBannerOpen(false);
    setModalOpen(false);
  };

  const refuseAll = () => {
    setConsent({ analytics: false, marketing: false, preferences: false, necessary: true });
    setBannerOpen(false);
    setModalOpen(false);
  };

  const saveChoices = () => {
    setConsent({ 
      analytics: choices.analytics, 
      marketing: choices.marketing, 
      preferences: choices.preferences, 
      necessary: true 
    });
    setBannerOpen(false);
    setModalOpen(false);
  };

  const openModal = () => {
    setBannerOpen(false);
    setModalOpen(true);
  };

  // Make manage cookies link globally accessible
  useEffect(() => {
    const link = document.getElementById('cw-manage-cookies');
    if (link) {
      link.onclick = (e) => {
        e.preventDefault();
        const saved = getConsent();
        if (saved) {
          setChoices({
            analytics: saved.analytics,
            marketing: saved.marketing,
            preferences: saved.preferences
          });
        }
        setModalOpen(true);
      };
    }
  }, []);

  return (
    <>
      {/* Consent Banner */}
      {bannerOpen && (
        <div 
          role="dialog" 
          aria-live="polite" 
          aria-label="Consentement aux cookies"
          className="fixed inset-x-0 bottom-0 z-[60] bg-background/95 backdrop-blur border-t shadow-2xl"
        >
          <Card className="m-4 border-0 bg-card/95 backdrop-blur">
            <div className="p-6 max-w-6xl mx-auto">
              <div className="flex flex-col gap-4">
                <div>
                  <h2 className="text-lg font-semibold mb-2">Respect de votre vie privée</h2>
                  <p className="text-sm text-muted-foreground">
                    Nous utilisons des cookies pour mesurer l'audience, personnaliser votre expérience et améliorer nos services. 
                    Vous pouvez accepter tous les cookies, les refuser ou personnaliser vos préférences.
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button onClick={acceptAll} className="bg-primary hover:bg-primary/90">
                    Tout accepter
                  </Button>
                  <Button variant="outline" onClick={refuseAll}>
                    Tout refuser
                  </Button>
                  <Button variant="ghost" onClick={openModal}>
                    Personnaliser
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Les cookies essentiels sont toujours actifs. Version {CONSENT_VERSION}.{' '}
                  <a href="/politique-confidentialite" className="underline hover:no-underline">
                    En savoir plus
                  </a>
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Preferences Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Préférences de confidentialité</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <p className="text-sm text-muted-foreground">
              Gérez vos préférences de cookies par catégorie. Les cookies essentiels sont toujours actifs 
              car nécessaires au fonctionnement du site.
            </p>
            
            <div className="space-y-4">
              {/* Necessary - Always enabled */}
              <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/50">
                <Checkbox checked disabled />
                <div className="flex-1">
                  <label className="text-sm font-medium">
                    {CATEGORIES.necessary.label}
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Indispensables au fonctionnement du site (navigation, sécurité, préférences de base)
                  </p>
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-center space-x-3 p-3 rounded-lg border">
                <Checkbox 
                  checked={choices.analytics}
                  onCheckedChange={(checked) => 
                    setChoices(prev => ({ ...prev, analytics: !!checked }))
                  }
                />
                <div className="flex-1">
                  <label className="text-sm font-medium cursor-pointer">
                    {CATEGORIES.analytics.label}
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Mesure d'audience anonyme pour améliorer le site (Google Analytics)
                  </p>
                </div>
              </div>

              {/* Marketing */}
              <div className="flex items-center space-x-3 p-3 rounded-lg border">
                <Checkbox 
                  checked={choices.marketing}
                  onCheckedChange={(checked) => 
                    setChoices(prev => ({ ...prev, marketing: !!checked }))
                  }
                />
                <div className="flex-1">
                  <label className="text-sm font-medium cursor-pointer">
                    {CATEGORIES.marketing.label}
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Publicité personnalisée et suivi des conversions (Meta, TikTok, Google Ads)
                  </p>
                </div>
              </div>

              {/* Preferences */}
              <div className="flex items-center space-x-3 p-3 rounded-lg border">
                <Checkbox 
                  checked={choices.preferences}
                  onCheckedChange={(checked) => 
                    setChoices(prev => ({ ...prev, preferences: !!checked }))
                  }
                />
                <div className="flex-1">
                  <label className="text-sm font-medium cursor-pointer">
                    {CATEGORIES.preferences.label}
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Contenu enrichi et médias intégrés (YouTube, Vimeo)
                  </p>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="outline" onClick={refuseAll}>
                Tout refuser
              </Button>
              <Button onClick={saveChoices}>
                Enregistrer mes préférences
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}